// Creators — API 계약 (초안) + mock 스위치.
//
// 목적: 6개 섹션(Overview/Profile/Performance/Products/Campaigns/Financial)이
//   백엔드(commerce admin BFF, apps-api)와 주고받을 데이터 "모양"을 프론트가
//   먼저 확정한다. 백엔드 연동 전에는 mock 을 반환해 UI 를 병렬로 개발한다.
//
// 인증/호출 패턴은 creator-slots(../creator-slots/creatorSlots.ts)와 동일:
//   same-origin 경로 → 포털 서버 프록시가 apps-api 로 Bearer 포워딩.
//   토큰 없음(로컬) 또는 미구현 엔드포인트 → mock 으로 디자인 미리보기.
//
// ── 담당(소유) ────────────────────────────────────────────────
//   현정: overview · performance · profile
//   혜원: campaigns · financial · rateCard (products)
// 계약(타입)은 공유 파일이므로 변경은 작은 PR + 양쪽 리뷰.
//
// ── 계약 변경 이력 ────────────────────────────────────────────
//   [2026-08 현정, Overview 게이트A-5 · 혜원 알림] additive 3필드 +
//   포맷/주석 정정. 모두 optional 또는 주석/단위 명시라 기존 소비 무영향:
//     · CreatorSummary.photoUrl?         (히어로 프로필 사진)
//     · BrandedContentItem.aspectRatio?  (카드 종횡비 힌트)
//     · ConfirmedDeal.status?            (StageName 매핑 라벨)
//     · ConfirmedDeal.date = YYYY-MM(통일) / RevenuePoint.revenue·cumulative = 억 원
//     · watchRate·brandedEngagement 0~1 상한 제거(숏폼 >1), 분모0 가드는 UI에서 "—"
//   [2026-08 QA 정정 · 혜원 알림] BrandedContentItem.watchRate = "시청률"(조회수/구독자)로 확정.
//     스캐폴드가 인게이지먼트((좋아요+댓글)/조회수)로 잘못 정의했던 것 정정 →
//     ChannelPerformance.watchRate와 동일 정의(사용자 유일 기준). 값 계산은 BE 몫.
//     (brandedEngagement는 그대로 인게이지먼트 = 별개 지표.)
//   [2026-08 현정, SNS 데이터 유효성 v1 · 혜원 알림] additive only — 신규 필드는 전부
//     optional이고 기존 필드는 삭제·타입 변경 0. BE(cic) 선배포 전에도 화면 동작 불변(R9).
//     · MetricReason / FormatPair / DataMeta / ConfirmDays / DataFreshness 타입 신설
//     · CreatorSummary.dataFreshness?            (사이드바 신선도 배지 — 축 A)
//     · ChannelSubs.lastCollectedAt? / autoCollect?  (autoCollect = 기존 linked와 값 동일·정직한 이름)
//     · CreatorListItem.lastCollectedAt?         (목록 카드 지연 마커)
//     · OverviewData.meta? / brandedEngagementSampleSize? / ChannelPerformance.sampleSize?
//     · ProfileData.channelScale.channels[].lastCollectedAt? / lastMediaCollectedAt? /
//       lastMediaAt? / viewsUnavailableReason?
//     · PerformanceData.meta? / sampleSize? / median? / confirmDays? /
//       coverage[].rows? · lastMediaAt? · reason?
//   [2026-08 현정, BE Phase 1 계약 확정분 · 혜원 알림] additive only:
//     · PerformanceData.kpiReason?          (지표별 롱/숏 결측 사유)
//     · ChannelPerformance.reason?          (채널별 롱/숏 결측 사유)
//   [2026-08 현정, QA 1차 반려 D1·D4 · 혜원 알림]
//     · confirmDays 경로 정정: PerformanceData 최상위 → **DataMeta.confirmDays** (BE 직렬화가 정본)
//     · ProfileData.meta? / history.milestones[].sampleSize? 추가 (집계 고지·이중 가드 표기)
//   [2026-08-11 현정, 활동 이력 자동 5축 · 혜원 알림] additive — milestone code 2종 확장
//     (union 확장이라 기존 소비 무영향, 렌더는 코드 무관 제네릭):
//     · 'brand_count'  누적 협업 브랜드 계단(10/30/50/100 최고 계단 "N+"), 연도 없음 → "현재" 배지
//     · 'collab_years' 브랜드 협업 N년차(만 2년 이상만), year = 첫 협찬 연도 → 타임라인
//     스펙: docs/superpowers/specs/2026-08-11-creators-workspace-tabs-spec.md §4-⑥
//   [2026-08-11 현정, Profile 오디언스 인구통계 · 혜원 알림] additive — 신규 타입·함수만(기존 무변경):
//     · AudienceDemographics / AudienceDistributionEntry + fetchAudience()
//     · 소스가 cic 아님 — 포털서버 Mongo(OAuth 연동 크리에이터 한정). null → 섹션 생략
import { ApiError, getLecommendToken } from '../platform/creatorBusiness'
export { ApiError, setLecommendToken } from '../platform/creatorBusiness'

// 워크스페이스 데이터 API — /api/creators/* (SPA 라우트 /creators/:id 와 충돌 방지 위해 /api 하위로.
//   /api 는 포털서버(3001)에서 auth-gate + 프록시. 기존 /creators/:id 프록시 정규식은 제거).
const BASE_URL = '/api/creators'

/**
 * 실 API vs mock 판단. 이 앱은 항상 포털 안에서 구동되고, 포털 프록시가 인증을
 * 처리한다(dev = vite 프록시가 토큰 주입 · prod = 서버 프록시가 SSO 쿠키 전달).
 * 따라서 localStorage 토큰 없이도 실 API 를 쓴다. (mock 은 프록시 없는 순수
 * 디자인 프리뷰용 잔재 — 실사용 경로에선 항상 실 API)
 */
export function useMock(): boolean {
  return false
}

interface Envelope<T> {
  success: boolean
  data?: T
  errorMessage?: string
}

async function apiGet<T>(path: string, params?: Record<string, string | undefined>): Promise<T> {
  const qs = new URLSearchParams()
  if (params) for (const [k, v] of Object.entries(params)) if (v) qs.append(k, v)
  const token = getLecommendToken()
  const res = await fetch(`${path}${qs.toString() ? `?${qs}` : ''}`, {
    // leferi_sso 쿠키를 프록시(→포털서버 3001)로 전달 → 서버가 쿠키를 Bearer로 주입.
    // (same-origin이라 기본 전송되지만 명시. Authorization 헤더는 토큰 있을 때만 폴백.)
    credentials: 'include',
    headers: { Accept: 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
  })
  let body: Envelope<T> | null = null
  try {
    body = (await res.json()) as Envelope<T>
  } catch {
    /* non-JSON */
  }
  // 폴백 문구엔 상태코드를 넣지 않는다 — 표시 지점에서 transport 실패일 때만 (status)를 1회 붙임.
  //   transport 실패(401·!ok·non-JSON) = 코드 유의미 / envelope 실패(200 + success:false) = 코드 무의미.
  if (res.status === 401) throw new ApiError('로그인이 필요합니다.', 401, true)
  if (!res.ok) throw new ApiError(body?.errorMessage ?? '요청 실패', res.status, true)
  if (!body) throw new ApiError('응답을 해석하지 못했습니다.', res.status, true) // 2xx인데 non-JSON
  if (!body.success) throw new ApiError(body.errorMessage ?? '요청 실패', res.status, false) // envelope 실패
  return body.data as T
}

// ─────────────────────────────────────────────────────────────
// 공통
// ─────────────────────────────────────────────────────────────
export type Platform = 'youtube' | 'instagram' | 'tiktok'
export type ContentFormat = 'long' | 'short'

// ── SNS 데이터 유효성 v1 (2026-08) 공통 타입 — 전부 additive · optional ──
/** 값이 없는(또는 못 믿는) 이유. 어휘의 단일 원천은 BE `reason` 코드이고 FE는 매핑만 한다.
 *  임계(표본 5건·D+3/D+14) 적용만 FE 몫 — 사실은 BE, 표시 정책은 FE. */
export type MetricReason =
  | 'low_sample' // 표본 부족(n<5). n=0이면 "집계 콘텐츠 없음" 분기
  | 'not_collected' // 자동 수집 대상 아님
  | 'platform_unsupported' // 구조적 부재(IG/TikTok 채널 누적 조회수 등)
  | 'pending_confirmation' // 대상 콘텐츠는 있으나 전량 확정 전
  | 'never_collected' // 수집 대상이나 최초 수집 전

/** 롱/숏 쌍 — 표본 수·중앙값 등 additive 수치 공용. 값 없으면 null. */
export type FormatPair = Record<ContentFormat, number | null>

/** 롱/숏 결측 사유 쌍. BE 보장 2가지 —
 *  ① 값이 있으면 **항상 null**(저신뢰 표기는 sampleSize가 담당한다. 이 필드는 "왜 비었나"만 답한다)
 *  ② 값이 없으면 **항상 non-null**(모순 케이스도 low_sample로 떨어진다 → 이유 없는 결측 0) */
export type ReasonPair = Record<ContentFormat, MetricReason | null>

/** 집계 기준 메타(축 B — 콘텐츠/브랜디드 지표). windowMonths 없으면 화면은 "전체 기간"으로 표기. */
export interface DataMeta {
  windowMonths?: number | null // 집계창(개월). 확정 12
  source?: string // 소스 테이블명 — 화면 미표기(시스템명 비노출 관례)
  lastSyncedAt?: string | null // YYYY-MM-DD. 집계 기준 시각(축 B)
  lastMediaAt?: string | null // YYYY-MM-DD. 최신 집계 콘텐츠 발행일(축 B')
  /** 확정일 파생 표기용 — 화면의 "발행 N일"은 이 값에서만 나온다(하드코딩 금지).
   *  ⚠️ 경로는 **`meta.confirmDays`**다. BE 직렬화 테스트가 이 경로를 고정하고 있어 이쪽이 정본
   *  (기획 §6.1의 top-level 표기는 구현과 어긋난 채로 양쪽 테스트를 통과해 런타임에서만 깨졌다). */
  confirmDays?: ConfirmDays
}

/** 확정일 파생 표기용 — 화면의 "발행 N일"은 이 값에서만 나온다(하드코딩 금지). */
export interface ConfirmDays {
  min: number
  max: number
}

/** 축 A — 채널 지표(팔로워·콘텐츠수·누적조회) 수집 신선도.
 *  status 필드는 두지 않는다: 날짜만 내리고 임계 판정은 FE가 한다. */
export interface DataFreshness {
  oldestCollectedAt?: string | null // YYYY-MM-DD. 대상 채널 중 가장 오래된 값(보수적)
  autoCollectChannels?: number // 자동 수집 대상 채널 수. 0이면 배지 "대상 없음"
  channels?: FreshnessChannel[] // hover 목록
}
export interface FreshnessChannel {
  platform: string // 화이트리스트 밖 값(x 등) 유입 대비 string
  handle: string
  lastCollectedAt: string | null // 축 A
  lastMediaCollectedAt: string | null // 축 A' — 게시물 수집 완료일
  /** ⚠️ 축 A(채널 지표 수집 대상) 기준. overview `channels[].autoCollect`(축 A')와 다른 축이다 — 혼용 금지. */
  autoCollect: boolean
}

/** 좌측 히어로 + 크리에이터 스위처가 쓰는 최소 식별 정보. */
export interface CreatorSummary {
  id: string
  name: string
  handle: string
  label: string // 예: LBE
  tagline: string // 예: Beauty · Lifestyle Editor
  themeColor: string // hex — 추후 ADMIN 이미지에서 자동추출. 히어로 틴트는 이 값에서 hexA로 파생.
  photoUrl?: string // additive(게이트A-5, 혜원 알림) — SF 이미지/채널 썸네일. 없으면 히어로 다크 배경 폴백
  channels: ChannelSubs[]
  dataFreshness?: DataFreshness // additive — 사이드바 신선도 배지. 없으면 배지 미표시(현행 유지)
}

export interface ChannelSubs {
  platform: Platform
  handle: string
  subscribers: number
  linked: boolean // 게시물 자동 수집 대상(creator_social.media_auto_updatable) — 오디언스 스트립 도트. 정직한 이름은 아래 autoCollect
  main: boolean // 주력 채널(SF Type__c) — 화면 미사용(연동 표시로 복귀, 2026-08-07). 계약 미러 유지
  /** additive — ⚠️ 축 A'(게시물 수집 대상 = 기존 linked와 값 동일). 미도달 시 linked 폴백.
   *  summary `dataFreshness.channels[].autoCollect`(축 A · 채널 지표)와 다른 축이다. */
  autoCollect?: boolean
  lastCollectedAt?: string | null // additive — 축 A(채널 지표) 수집일(YYYY-MM-DD)
}

// ─────────────────────────────────────────────────────────────
// 크리에이터 목록 (/creators 첫 화면) — 검색·정렬·11축 필터·커서 인피니트
//   [2026-08 현정 신규, 혜원 공유] additive. GET /api/creator-list (proposal 2026-08-07 §4).
// ─────────────────────────────────────────────────────────────
export type CreatorSort = 'subs' | 'name' | 'recent'

// BE 계약(CreatorListView.ItemView, 커밋 81a08a9b3) 1:1.
export interface CreatorListItem {
  id: string
  name: string // creator.nickname (표시 정제 BE 적용)
  label: string | null // leferi_creator_department (SF 정답). 없으면 카드 "레이블 미지정"
  contractType: string | null // leferi_creator_type 한글 원값(파트너/전속/단건/네트워크/해지)
  photoUrl: string | null // picture → social_thumbnail 폴백
  subscribers: number // 대표 채널 max follower
  scaleLabel: string | null // 규모 한글 라벨(메가/탑/미들/마이크로) — 카드엔 메가/탑만 강조
  platforms: string[] // 보유 플랫폼(youtube/instagram/tiktok)
  lastUploadAt: string | null // max media_at (YYYY-MM-DD)
  lastCollectedAt?: string | null // additive — 축 A 수집일. 카드 지연 마커(D+14 이상)용. 없으면 마커 미표시
}

/** 다중 선택 필터 축(전부 optional·CSV 직렬화). null/미지정 레이블은 값 '__none__'. */
export interface CreatorListParams {
  q?: string
  sort?: CreatorSort
  statuses?: string[]
  scales?: string[]
  labels?: string[]
  genres?: string[]
  languages?: string[]
  personalColors?: string[]
  skinTypes?: string[]
  skinWorries?: string[]
  preferenceIngredients?: string[]
  dislikeIngredients?: string[]
  businessTypes?: string[]
  cursor?: string
  limit?: number
}

export interface CreatorListResponse {
  items: CreatorListItem[]
  nextCursor: string | null // null=끝
  total: number // 현재 필터 기준 매칭 총계(화면 표기·카운트 기준)
  activeTotal: number // 해지 제외 총원(필터 무관 기준선). 표기는 total 기준 통일
  nullExcluded: Record<string, number> // 적용된 저채움 축별 미입력 제외 수(고지 배너)
}

export interface CreatorFilterOption {
  value: string
  label: string
  count?: number
}
/** filter-options 응답 — 라이브 distinct(하드코딩 금지). 축별 옵션 목록. */
export interface CreatorListFilters {
  statuses: CreatorFilterOption[]
  scales: CreatorFilterOption[]
  labels: CreatorFilterOption[]
  genres: CreatorFilterOption[]
  languages: CreatorFilterOption[]
  personalColors: CreatorFilterOption[]
  skinTypes: CreatorFilterOption[]
  skinWorries: CreatorFilterOption[]
  preferenceIngredients: CreatorFilterOption[]
  dislikeIngredients: CreatorFilterOption[]
  businessTypes: CreatorFilterOption[]
}

// ─────────────────────────────────────────────────────────────
// ① Overview (현정)
// ─────────────────────────────────────────────────────────────
export interface OverviewData {
  brandedEngagement: number // 협찬 전량(롱+숏) (좋아요+댓글)/조회수 평균. 숏폼 한정은 2026-08-12 제거(BE 정합). 분모0/null 가드(비유한값이면 UI "—")
  totalSubscribers: number
  channels: ChannelSubs[]
  brandedContent: BrandedContentItem[]
  channelPerformance: ChannelPerformance[]
  revenueTrend: RevenuePoint[] // 월별
  confirmedBusiness: ConfirmedDeal[]
  recentCollaborations: CollaborationItem[]
  brandedEngagementSampleSize?: number | null // additive — 히어로 배지 모수. n<5면 "표본 N건" 캡션
  meta?: DataMeta // additive — 집계창·수집 기준일. 없으면 관련 고지 미표시(현행 유지)
  /** additive — 채널별 조각. 세 채널분이 한 응답에 담긴다(탭은 조회 없이 고르기만).
   *  미도달(구버전 BE)이면 최상위 필드로 폴백한다 — 배포 순서와 무관하게 화면이 안 빈다. */
  byPlatform?: Partial<Record<Platform, OverviewSlice>>
}
/** 채널별로 갈리는 값만 담는 조각. 나머지(채널·매출·Confirmed)는 채널과 무관해 최상위에만 있다. */
export interface OverviewSlice {
  brandedEngagement: number
  brandedEngagementSampleSize: number
  brandedContent: BrandedContentItem[]
  meta?: DataMeta
  /** 이 채널의 최근 협업. 최상위 `recentCollaborations` 는 소개서용 세 채널 합본이다. */
  recentCollaborations: CollaborationItem[]
}
export interface BrandedContentItem {
  id: string
  platform: Platform
  title: string
  brand: string
  product: string
  views: number
  watchRate: number // 시청률 = 조회수/구독자 (숏폼 >1 정상, 상한 없음). 분모0 가드 시 UI "—". ChannelPerformance.watchRate와 동일 정의
  thumbnailUrl?: string
  aspectRatio?: number // additive(게이트A-5, 혜원 알림) — 카드 종횡비 힌트(shorts≈0.56 / long≈1.78). 표시용
  url?: string | null // additive — 원본 영상 링크. 있으면 카드 클릭 시 새 탭 이동, null이면 비클릭
}
export interface ChannelPerformance {
  platform: Platform
  subscribers: number
  avgViews: Record<ContentFormat, number>
  watchRate: Record<ContentFormat, number> // 구독자 대비 시청률. 숏폼은 >1 정상(상한 없음). 분모0 가드
  sampleSize?: FormatPair // additive — 채널 행 표본 수. 없으면 표본 표기 미노출(현행 유지)
  reason?: ReasonPair // additive — 값이 없을 때의 사유(avgViews/watchRate와 같은 롱/숏 맵)
}
export interface RevenuePoint {
  month: string // YYYY-MM
  revenue: number // 단위=억 원(소수 허용). 블록⑤는 현정=mock, 실데이터는 혜원 Financial
  cumulative: number // 누적, 단위=억 원
}
export interface ConfirmedDeal {
  brand: string
  scope: string
  date: string // YYYY-MM (게이트A-7 통일)
  active: boolean
  status?: string // additive(게이트A-5, 혜원 알림) — StageName 매핑 라벨(예: Confirmed / In prep)
}
export interface CollaborationItem {
  platform: Platform
  brand: string
  title: string
  views: number
  thumbnailUrl?: string
  url?: string | null // additive — 원본 영상 링크(RECENT 카드 클릭 시 새 탭). null이면 비클릭
  publishedAt?: string | null // additive(2026-08-18) — 발행일 "YYYY-MM-DD". 소개서가 세 채널 합본을 날짜순으로 세우는 데 쓴다
}

// ─────────────────────────────────────────────────────────────
// ② Profile (현정)
// ─────────────────────────────────────────────────────────────
// 2026-08 Profile 탭 구현 — proposal.md §3 대로 additive 확장(혜원 리뷰용).
//   기존 agency/partnershipTier(미출시 스캐폴드값)를 실 소스 기반 필드로 초기 확정.
//   전부 신규 또는 optional → 기존 Overview/Campaigns/Financial 소비 무영향.
export interface ProfileData {
  // ── 상단 4타일 ──
  isLeferi: boolean // 소속 타일 서브값 = creator.leferi_creator 플래그
  label?: string // 소속 타일 메인값 = leferi_creator_department(←SF Leferi_Label__c, LBE/Caape! 등). null이면 "Leferi 소속"만 폴백
  lmpaParticipant?: boolean // 파트너십 타일 = LMPA 참여 여부(SF Opportunity RecordType 파생). Tier 등급은 보류(원천 부재)
  categories: string[] // 주력 카테고리 = SF Contents_Genre__c(1순위) / bi_product_category 파생(2순위)
  activeChannels: Platform[] // creator_social.social_type distinct (Overview 재사용)

  // ── 기본 정보 메타 (additive) ──
  contractType?: string // leferi_creator_type (파트너/전속/해지). 라벨 표시용
  country?: string // creator.country (ISO3)
  language?: string // creator.language
  manager?: string // 담당 매니저 = SF Owner.Name (CreatorsView.manager). 🔒 퍼블릭 프로필 생성 시 제외(연락처류 취급)

  // ── 뷰티 속성 (additive) — SF 실데이터(CreatorsView/CreatorDto가 이미 서빙) ──
  beautyAttributes: {
    personalColor?: string // SF Personal_Color__c
    skinType?: string // SF Skin_Type__c
    skinWorry?: string // SF Skin_Worry__c
    preferenceIngredient?: string // SF Preference_Ingredient__c
    dislikeIngredient?: string // SF Dislike_Ingredient__c
  }

  // ── 채널 규모 (additive) — 규모 스냅샷만(성장률·인게이지먼트·demographics 범위 밖) ──
  channelScale: {
    totalSubscribers: number // Σ follower (Overview와 동일 계산)
    scaleLabel?: string // 채널 규모 등급 라벨(BE CreatorChannelScale: 마이크로/미들/탑/메가, max(YT,IG) 기준). BE 응답 필드명 = channelScale.scaleLabel. null이면 배지 미표시
    channels: {
      platform: Platform
      name?: string // creator_social.social_name (폴백 SF youtube/instagram Link)
      url?: string // creator_social.social_url (전 플랫폼) — 바로가기 링크
      subscribers: number // creator_social.follower
      contents: number // creator_social.media (영상/콘텐츠 수)
      // creator_social.view 누적. YouTube만 제공되고 IG/TikTok은 **플랫폼이 채널 누적 조회수를
      // 내주지 않아** 구조적으로 null이다(상태 ④ 미제공 — 연동으로 해소되는 값이 아니다).
      views: number | null
      lastCollectedAt?: string | null // additive — 축 A(채널 지표) 수집일. `갱신` 컬럼
      lastMediaCollectedAt?: string | null // additive — 축 A'(게시물) 수집일. 갱신 셀 title 보조
      lastMediaAt?: string | null // additive — 최신 게시물 발행일
      viewsUnavailableReason?: MetricReason // additive — 누적조회 "—"의 사유(각주는 블록 1회 집약)
    }[]
  }

  // ── 대표 콘텐츠: Overview와 소스 공유 (additive, 형태 재사용) ──
  representativeContent: BrandedContentItem[] // bi_base_data sponsored, 조회수순

  // ── 협업 브랜드: BI 소스 (additive) ──
  collaborationBrands: { brand: string; contentCount?: number; lastAt?: string }[] // BI sponsor_brand distinct 전량(FE는 패널 내부 스크롤)

  // ── 활동 이력: 자동 마일스톤 + 수동 (additive) ──
  history: {
    milestones: {
      // 자동 파생 — 현재값 기반
      code:
        | 'sub_silver'
        | 'sub_gold'
        | 'sub_diamond'
        | 'top_view_1m'
        | 'top_view_10m'
        | 'branded_pctile'
        | 'brand_count' // additive(2026-08 자동 5축) — 누적 협업 브랜드 계단(10/30/50/100), 연도 없음("현재")
        | 'collab_years' // additive(2026-08 자동 5축) — 브랜드 협업 N년차, year=첫 협찬 연도
      label: string // 표시 문구 (예: "YouTube 실버 버튼 달성")
      year?: string // 산출 가능 시만(top_view 업로드연도 근사). sub_*/branded_pctile은 생략 → "달성"/"현재"
      note?: string // 보조 (예: "상위 1%")
      /** additive — 산출 모수. branded_pctile은 BE의 모수 30 가드와 화면의 n<5 캡션이 **이중 가드**라,
       *  모수를 드러내야 어느 가드가 이겼는지 검증할 수 있다(미도달 시 캡션 없음 = 현행). */
      sampleSize?: number
    }[]
    manual: { year: string; note: string }[] // creator_dashboard_profile.notable_history (수동)
  }

  // ── 집계 메타 (additive) — 대표 콘텐츠·채널 규모 블록의 소스·집계창·기준일 고지에 쓴다 ──
  meta?: DataMeta
  /** additive — 채널별 조각. 세 채널분이 한 응답에 담긴다(탭은 조회 없이 고르기만).
   *  대표 콘텐츠와 협업 브랜드가 각자 다른 채널을 볼 수 있어야 해서 둘 다 미리 와 있어야 한다. */
  byPlatform?: Partial<Record<Platform, ProfileSlice>>
}
/** 채널별로 갈리는 값만 담는 조각. `history` 는 `brand_count` 계단만 채널을 타지만 한 덩어리로 만들어진다. */
export interface ProfileSlice {
  representativeContent: ProfileData['representativeContent']
  collaborationBrands: ProfileData['collaborationBrands']
  collaborationBrandsTotal: number
  history: ProfileData['history']
  /** 이 채널의 근거 메타. 최상위 `meta` 는 bi_base_data(YouTube 전용 레거시) 기준이라
   *  IG·TikTok 블록에서 쓰면 남의 채널 동기화 시각을 자기 것처럼 단언한다. BE 는 채널별로
   *  원천을 갈라 이미 내려주고 있다(IG·TikTok = creator_social_media). */
  meta?: DataMeta
}

// ─────────────────────────────────────────────────────────────
// ③ Performance (현정)
// ─────────────────────────────────────────────────────────────
export interface PerformanceData {
  avgViews: Record<ContentFormat, number | null> // bi_base_data 전체 shorts별(YT). null=미수집("—")
  brandedAvgViews: Record<ContentFormat, number | null> // sponsored=1 (YT)
  engagement: Record<ContentFormat, number | null> // (좋아요+댓글[+공유])/조회수, shorts별(YT). 숏폼 >1 가능(상한 없음)
  brandedViewTrend: { month: string; long: number | null; short: number | null }[] // sponsored 월별(YT). 월값 null=미수집(0 아님)
  businessTrend: { month: string; revenue: number | null; count: number | null }[] // 매출(억)·건수 — BE가 Financial(SF T_Sales__c)을 월별 재집계(2026-08-13). 빈 배열=SF 링크 없음
  hubPrograms: HubProgramPerf[] // portal 프록시가 /api/creators/:id/performance 응답을 Mongo 집계로 enrich (cic는 빈 배열). [혜원 공유]
  // 플랫폼 수집 커버리지 — 헤더 고지의 유일한 입력. undefined=구버전 BE(현행 문구 폴백) / []=연결된 채널 없음.
  coverage?: {
    platform: Platform
    supported: boolean // 실측(집계창 내 rows ≥ 5). 값 가드와 동일 임계
    rows?: number // additive — 집계창 내 **영상 수**(중복 제거 후. 원시 행 수가 아니다)
    lastMediaAt?: string | null // additive — 집계창과 무관한 최신 집계 콘텐츠 발행일
    reason?: MetricReason // additive — supported=false 사유
  }[]
  sampleSize?: {
    // additive — KPI 3분할 모수. 없으면 캡션 미표시(현행 유지)
    avgViews?: FormatPair
    brandedAvgViews?: FormatPair
    engagement?: FormatPair // 인게이지먼트는 조회수>0 영상만 → 평균 조회수와 모수가 다르다
  }
  median?: {
    // additive — 단순평균의 아웃라이어 민감성 보정용 병기(ⓘ 전용)
    avgViews?: FormatPair
    brandedAvgViews?: FormatPair
  }
  kpiReason?: {
    // additive — KPI 3분할의 롱/숏 결측 사유. coverage 사유 폴백보다 우선한다
    avgViews?: ReasonPair
    brandedAvgViews?: ReasonPair
    engagement?: ReasonPair
  }
  meta?: DataMeta // additive — 집계창·수집 기준일·확정일(meta.confirmDays). 헤더 문구가 이 값에서 파생된다
  // additive — 이 응답이 어느 플랫폼 소스에서 나왔는가. 요청값이 아니라 BE가 실제로 조회한 값이라
  // 오타(?platform=foo)가 흡수됐을 때도 화면이 진짜 탭을 안다.
  platform?: Platform
  // additive — KPI 산식 근거. 탭마다 다르다("발행 확정 조회수" / "최신 누적 조회수").
  // IG/TikTok은 게시물당 추이 스냅샷이 1~2개뿐이라 확정 조회수를 복원할 수 없다.
  basis?: string
  /** additive — 채널별 조각. 세 채널분이 한 응답에 담긴다(탭은 조회 없이 고르기만).
   *  각 조각의 `coverage` 는 **그 채널을 명시 조회해 만든 것**이라 자기 행이 정확하다. */
  byPlatform?: Partial<Record<Platform, PerformanceSlice>>
}
/** 채널별로 갈리는 값만 담는 조각. businessTrend(Salesforce)·meta 는 채널 무관이라 최상위에만 있다. */
export interface PerformanceSlice {
  avgViews: Record<ContentFormat, number | null>
  brandedAvgViews: Record<ContentFormat, number | null>
  engagement: Record<ContentFormat, number | null>
  brandedViewTrend: PerformanceData['brandedViewTrend']
  coverage: NonNullable<PerformanceData['coverage']>
  sampleSize: NonNullable<PerformanceData['sampleSize']>
  median: NonNullable<PerformanceData['median']>
  kpiReason: NonNullable<PerformanceData['kpiReason']>
  basis: string
  /** 이 채널의 근거 메타. 최상위 `meta` 는 bi_base_data(YouTube 전용) 기준이라 IG·TikTok 탭에서
   *  쓰면 확정일이 세 탭에서 똑같이 나온다 — 실제 확정 기준은 플랫폼마다 다르다
   *  (YouTube = engagement_days 실값 / Instagram = 5일 / TikTok = 2일). BE 는 채널별로 이미 내려준다. */
  meta?: DataMeta
}
/** @deprecated 2026-08-13 — Hub Program 트래킹이 포털 전용 API(`/api/hub-programs/*`)로 이동했다.
 *  cic 계약 필드라 타입은 남기되 화면은 소비하지 않는다(포털 enrich 경로도 함께 제거됨). */
export interface HubProgramPerf {
  name: string
  longAvg: number | null
  shortAvg: number | null
  trendPct: number | null
}

// ─────────────────────────────────────────────────────────────
// ③-1 Hub Program 트래킹 (현정) — ⚠️ 소스가 cic 가 아니라 **포털 Mongo**다.
//   `/api/hub-programs/*` (BASE_URL 아님 — dev 에서 /api/creators* 는 cic 로 직결 프록시되므로
//    포털 Mongo 라우트는 그 밖 네임스페이스를 쓴다. 서버 routes/creatorPrograms.ts 주석 참고)
//   연재 프로그램(레올영픽 등)의 회차별 성과. Products 탭의 `HubPackage`(단가표 묶음 상품)와
//   이름만 겹치는 **다른 개념**이다 — 혼동 금지.
// ─────────────────────────────────────────────────────────────

/** 채널 전체 평균(롱+숏, 1편당) — 프로그램 지표를 같은 정의로 비교하기 위한 기준선. */
export interface HubBaselineWindow {
  windowDays: number
  n: number // 표본 영상 수. 0이면 기준선 미표기(값 0과 구분)
  views: number
  viewRate: number // 조회수 ÷ 구독자 (%)
  watch: number // 평균 시청시간 ÷ 길이 = APV (%)
  subs: number
  eng: number // (좋아요+댓글) ÷ 조회수 (%)
  likes: number
  comments: number
  avgWatch: number
}
export interface HubChannelBaseline {
  subscribers: number
  /** '3m' | '6m' | '12m' — 키는 서버가 정한다(하드코딩 금지, 순회로 소비) */
  windows: Record<string, HubBaselineWindow>
}
/** 기간별 프로그램 지표. views/subs 는 회차 평균, *Total 은 그 기간 합계. */
export interface HubPeriodMetrics {
  views: number
  viewRate: number
  watch: number
  subs: number
  eng: number
  viewsTotal: number
  subsTotal: number
}
export interface HubProgramRow {
  id: string
  name: string
  alias: string // 별칭 표시용 (' · ' 조인)
  accent: string // 프로그램 컬러(hex)
  start: string | null
  startFmt: string | null
  daysSinceStart: number | null
  episodes: number
  lastUploadDaysAgo: number | null
  targetCadence: number // 목표 발행 주기(일)
  sparkData: Array<{ y: number }>
  notStarted: boolean // 회차 0 — "시작 전"으로 표기(지표 0 아님)
  views: number
  viewRate: number
  watch: number
  subs: number
  eng: number
  metrics: Record<string, HubPeriodMetrics> // 키 = overview.periods
  analyticsReady: boolean // false면 OAuth 지표 미수집(시청률·구독전환 "—")
}
export interface HubProgramsOverview {
  creatorId: string
  handle: string
  channelSubscribers: number
  periods: string[] // 총기간 · 최근 1달 · 분기 · 반기 · 연간
  channelBaseline: HubChannelBaseline
  list: HubProgramRow[]
}
/** channelLinked=false = 포털에 YouTube 채널이 없는 크리에이터(963명 중 대다수).
 *  "프로그램 0개"와 **다른 빈 상태**다 — 등록 자체가 불가능하므로 안내 문구가 달라야 한다. */
export interface HubProgramsResponse {
  channelLinked: boolean
  overview: HubProgramsOverview | null
}

export type HubSignalLevel = 'green' | 'yellow' | 'red'
/** 몰입(APV) × 반응(참여율) 4분면. CTR·노출은 YouTube Analytics API 미제공이라 이 두 축을 쓴다. */
export type HubQuadrant = 'all_round' | 'reactive' | 'immersive' | 'both_weak' | 'insufficient'
export interface HubEpisodeSignals {
  composite: HubSignalLevel
  compositeScore: number | null
  quadrant: HubQuadrant
  engagementRate: number | null
  diagnosis: string // 규칙 기반 한 줄 진단
  deltaVsPrev: number | null
  deltaVsAvg: number | null
}
export interface HubEpisodeAnalytics {
  ytVideoId: string
  episodeNumber: number | null
  publishedAt: string | null
  title: string
  thumbnailUrl: string
  durationSeconds: number
  viewCount: number
  likeCount: number
  commentCount: number
  windowViews: number | null
  avgViewDurationSec: number | null
  avgViewPercentage: number | null
  impressions: number | null // ⚠️ Analytics API 미제공 — 항상 null
  impressionsCtr: number | null // 〃
  shares: number | null
  likes: number | null
  comments: number | null
  /** 참여율 = (좋아요+댓글) ÷ 조회수 × 100(%) */
  engagementRate: number | null
  subsGained: number | null
  subsLost: number | null
  addedToPlaylists: number | null
  signals: HubEpisodeSignals
}
export interface HubKpiWithBaseline {
  value: number | null
  baseline: number | null
  deltaPct: number | null
  signal: HubSignalLevel | 'neutral'
}
export interface HubProgramDetails {
  program: { programId: string; name: string; color: string; aliases: string[] }
  episodeAnalytics: HubEpisodeAnalytics[]
  diagnostics: {
    kpis: {
      avgViews: HubKpiWithBaseline
      avgViewPercentage: HubKpiWithBaseline
      engagementRate: HubKpiWithBaseline
      netSubsPerEpisode: HubKpiWithBaseline
    }
    trend: {
      verdict: string
      level: HubSignalLevel
      prevAvg: number | null
      recentAvg: number | null
      deltaPct: number | null
      windowSize: number
    }
    weakness: { key: string; label: string; detail: string }
    successPatterns: Array<{ label: string; detail: string }>
    signalCards: Array<{ level: HubSignalLevel; title: string; body: string }>
  }
  cadenceAdherence: {
    targetIntervalDays: number | null
    actualAvgIntervalDays: number | null
    onScheduleRatio: number | null
  }
}

// ─────────────────────────────────────────────────────────────
// 영상 Analytics 지표 (현정) — ⚠️ 소스가 cic 가 아니라 **포털 Mongo**(YouTube OAuth 수집).
//   캠페인 화면의 조회수·시청률·전환을 이 한 번의 호출로 채운다. cic 는 구독전환·APV 를
//   구조적으로 낼 수 없다(Analytics API 는 채널 소유자 OAuth 전용).
//   경로가 /api/creators/* 밖인 이유는 dev vite 직결 프록시 회피 (서버 routes/videoMetrics.ts).
// ─────────────────────────────────────────────────────────────
export interface VideoMetric {
  views: number | null
  /** 시청률 = 조회수 ÷ 채널 구독자 × 100(%). 숏폼은 100% 초과 정상(상한 없음) */
  watchRate: number | null
  subscribers: number | null // 시청률 분모(근거 표기용)
  likes: number | null
  comments: number | null
  /** 참여율 = (좋아요+댓글) ÷ 조회수 × 100(%) */
  engagementRate: number | null
  subsGained: number | null
  subsLost: number | null
  /** 전환(구독전환) = gained - lost. YouTube 가 영상 단위로 집계한 순증 */
  netSubs: number | null
  /** 시청 지속률(APV, %) — watchRate 와 다른 지표 */
  avgViewPercentage: number | null
  avgViewDurationSec: number | null
  capturedAt: string | null
}

/** URL(형식 무관) 또는 videoId 배열 → { [videoId]: VideoMetric }.
 *  매칭 안 된 항목은 **키 자체가 없다**(0 아님 → 화면 "—"). 실패 시 빈 객체(화면은 graceful). */
export async function fetchVideoMetrics(
  urlsOrIds: string[],
): Promise<Record<string, VideoMetric>> {
  const ids = urlsOrIds.map((s) => (s ?? '').trim()).filter(Boolean)
  if (ids.length === 0) return {}
  try {
    const res = await apiGet<{ items: Record<string, VideoMetric> }>(
      `/api/video-metrics?ids=${encodeURIComponent(ids.join(','))}`,
    )
    return res.items ?? {}
  } catch {
    return {}
  }
}

/** 콘텐츠 URL에서 YouTube videoId 추출 — 서버와 같은 규칙(watch?v= · youtu.be · embed · shorts).
 *  유튜브가 아니면 null → 지표 조회 대상에서 제외한다. */
export function youtubeVideoId(url: string | null | undefined): string | null {
  const value = String(url ?? '').trim()
  if (!value || !/youtube\.com|youtu\.be/i.test(value)) return null
  const v = /[?&]v=([A-Za-z0-9_-]{6,})/.exec(value)
  if (v) return v[1]
  const path = value.split(/[?#]/)[0].replace(/\/+$/, '')
  const seg = path.slice(path.lastIndexOf('/') + 1)
  return /^[A-Za-z0-9_-]{6,64}$/.test(seg) ? seg : null
}

const HUB_URL = '/api/hub-programs'

export function fetchHubPrograms(creatorId: string): Promise<HubProgramsResponse> {
  return apiGet<HubProgramsResponse>(`${HUB_URL}/${encodeURIComponent(creatorId)}`)
}
export function fetchHubProgramDetails(
  creatorId: string,
  programId: string,
): Promise<HubProgramDetails> {
  return apiGet<HubProgramDetails>(
    `${HUB_URL}/${encodeURIComponent(creatorId)}/programs/${encodeURIComponent(programId)}`,
  )
}

// ─────────────────────────────────────────────────────────────
// ④ Campaigns (혜원)
// ─────────────────────────────────────────────────────────────
export type CampaignStage = 'intake' | 'casting' | 'production' | 'live' | 'complete'
export const CAMPAIGN_STAGES: CampaignStage[] = [
  'intake',
  'casting',
  'production',
  'live',
  'complete',
]
export const CAMPAIGN_STAGE_LABELS: Record<CampaignStage, string> = {
  intake: '문의',
  casting: '섭외',
  production: '제작',
  live: '발행',
  complete: '정산마감',
}
export interface Campaign {
  id: string
  brand: string
  title: string
  product?: string
  stage: CampaignStage
  progress: number // 0~8
  budget?: number
  budgetConfirmed: boolean // false = 예상(est.)
  openedDate: string // YYYY-MM-DD
  dueDate?: string
  owner: string
  isLmpa: boolean
  channels: CampaignChannel[]
  commerce?: { type: string; metric: string }
  isDraft?: boolean // true = SF 미연동 내부 드래프트 (id는 "draft:{n}")
}
export type CampaignChannelPlatform = Platform | 'commerce' | 'editorial'
export interface CampaignChannel {
  platform: CampaignChannelPlatform
  contentType: string
  url?: string
  views?: number
  watchRate?: number
  conversion?: number
}
export interface CampaignBoardKpi {
  inProgress: number
  dueThisWeek: number
  toUpload: number
  cumulativeViews: number
  commerceGmv: number
  totalRevenue: number
}
/** 캘린더 일정 — SF 기회상품 날짜(촬영/업로드/라이브/마감)에서 도출. date=YYYY-MM-DD. */
export interface CampaignScheduleEvent {
  date: string
  tag: string
  label: string
  brand: string
}
export interface CampaignBoard {
  kpi: CampaignBoardKpi
  campaigns: Campaign[]
  schedule: CampaignScheduleEvent[]
}
/** 신규 등록 payload (POST /creators/:id/campaigns). */
export interface NewCampaignInput {
  brand: string
  title: string
  owner: string
  periodStart: string
  periodEnd: string
  budget?: number
  startStage: CampaignStage
  channels: { platform: CampaignChannelPlatform; contentType: string; url?: string }[]
  commerceFusion: boolean
}

// ─────────────────────────────────────────────────────────────
// ⑤ Financial (혜원)
// ─────────────────────────────────────────────────────────────
export interface FinancialData {
  totalGmv: number // 원
  avgGmv: number // 원
  topProject: string // 라벨. 예: "espoir ₩1.82억"
  quarterly: { label: string; gmv: number }[] // 분기별 거래액 추이(원). label 예: "25 Q4"
  perProject: { project: string; date: string; gmv: number }[] // 원. date 예: "2025.10"
}

// ─────────────────────────────────────────────────────────────
// ⑥ Products / Rate Card (혜원)
// ─────────────────────────────────────────────────────────────
export interface RateCard {
  hubPackages: HubPackage[]
  channels: RateChannel[]
}
export interface HubPackage {
  id: string
  name: string
  items: string
  subtotal: number
  discount: number
  finalPrice: number
  revenueShare?: string
  notes: string[]
  lineItems: { label: string; price: number }[]
}
export interface RateChannel {
  key: Platform | 'license' | 'etc'
  name: string
  products: RateProduct[]
}
export interface RateProduct {
  id: string
  type: string // 상품 유형 (필수)
  sub?: string
  price: string // 자유 문자열 (예: ₩12,000,000 / 별도 협의)
  options: string[]
  note?: string // 강조 배지
  desc?: string
}
/** 상품 추가/편집 payload (PUT /creators/:id/rate-card/products). */
export interface RateProductInput {
  channelKey: RateChannel['key']
  type: string
  sub?: string
  price: string
  options: string[]
  note?: string
  desc?: string
}

// ─────────────────────────────────────────────────────────────
// fetch 함수 — 각 섹션 담당이 채운다. 지금은 mock 자동 폴백.
// dev 편의: 실 API 실패(토큰 만료/미구현 엔드포인트) 시 mock 으로 폴백해 로컬에서
// 화면이 항상 뜨게 한다. 프로드(import.meta.env.PROD)에선 폴백하지 않고 에러를 던진다.
// ─────────────────────────────────────────────────────────────
// 크리에이터 기본정보(이름·핸들·채널·themeColor) — 실 API만. mock 폴백 없음(가짜 김민지 제거).
// ⚠️ 계약 미비(2026-08 BE 확인): GET /creators/{id}(크리에이터 summary) 엔드포인트가 아직 없다.
//    apps bff엔 /api/creator-overview/{creatorId}(overview 데이터)뿐, 아이덴티티(name/handle/
//    label/tagline/themeColor)를 주는 소스가 없음. → 현재 이 호출은 에러 → 셸이 creator=null 처리
//    (히어로 미표시). 소스는 계약 결정 사항이라 임의 mock 넣지 않음. CTO/BE 결정 대기.
export async function fetchCreator(id: string): Promise<CreatorSummary> {
  return apiGet<CreatorSummary>(`${BASE_URL}/${id}`)
}
// Overview 데이터 — 실 API만 호출한다. mock 폴백 없음(사용자 결정).
//   · 200 → 실데이터  · 401 → 인증 필요(OverviewSection이 dev면 토큰 입력창 노출)
//   · 그 외/연결실패 → 에러 화면. 서버가 내려있으면 에러가 드러나 "진짜/가짜" 혼동 여지 제거.
export async function fetchOverview(id: string, platform?: Platform): Promise<OverviewData> {
  return apiGet<OverviewData>(`${BASE_URL}/${id}/overview`, { platform })
}
// Profile 데이터 — Overview와 동일하게 실 API만 호출한다. mock 폴백 없음(실데이터 철칙).
//   200 → 실데이터 · 401 → 인증 필요(ProfileSection이 dev면 토큰 입력창) · 그 외 → 에러 화면.
export async function fetchProfile(id: string, platform?: Platform): Promise<ProfileData> {
  return apiGet<ProfileData>(`${BASE_URL}/${id}/profile`, { platform })
}
// 오디언스 인구통계 — cic 가 아니라 포털서버 Mongo(ChannelDemographicsSnapshot, 대시보드 동일 소스).
//   OAuth 연동 크리에이터만 데이터 존재. null = 미연동/무데이터 → 섹션 자체 생략(빈 칸 금지).
//   부가 블록이라 실패도 null 로 삼킨다 — Profile 본문을 절대 깨지 않는다.
export interface AudienceDistributionEntry {
  key: string // 예: "female" | "age25-34" | "KR" (YouTube Analytics 버킷 키 verbatim)
  viewerPercentage: number // 0~100
}
export interface AudienceDemographics {
  capturedAt: string | null // 스냅샷 수집 시각(ISO) — 화면엔 "Snapshot · YYYY-MM-DD"로 고지
  gender: AudienceDistributionEntry[]
  ageGroup: AudienceDistributionEntry[]
  country: AudienceDistributionEntry[] // 상위 5개국
}
export async function fetchAudience(id: string): Promise<AudienceDemographics | null> {
  try {
    return await apiGet<AudienceDemographics | null>(`${BASE_URL}/${id}/audience`)
  } catch {
    return null
  }
}

// 크리에이터 목록 — 실 API만(실데이터 철칙). 커서 인피니트. GET /api/creator-list.
//   다중 필터는 CSV 직렬화, 빈 값은 생략. 정렬 기본 subs, limit 기본 20.
const LIST_URL = '/api/creator-list'
export async function fetchCreatorList(params: CreatorListParams): Promise<CreatorListResponse> {
  const qs = new URLSearchParams()
  const put = (k: string, v?: string) => {
    if (v) qs.append(k, v)
  }
  const putArr = (k: string, v?: string[]) => {
    if (v && v.length) qs.append(k, v.join(','))
  }
  put('q', params.q?.trim() || undefined)
  put('sort', params.sort)
  putArr('statuses', params.statuses)
  putArr('scales', params.scales)
  putArr('labels', params.labels)
  putArr('genres', params.genres)
  putArr('languages', params.languages)
  putArr('personalColors', params.personalColors)
  putArr('skinTypes', params.skinTypes)
  putArr('skinWorries', params.skinWorries)
  putArr('preferenceIngredients', params.preferenceIngredients)
  putArr('dislikeIngredients', params.dislikeIngredients)
  putArr('businessTypes', params.businessTypes)
  put('cursor', params.cursor)
  put('limit', String(params.limit ?? 20))
  return apiGet<CreatorListResponse>(`${LIST_URL}?${qs.toString()}`)
}
export async function fetchCreatorListFilters(): Promise<CreatorListFilters> {
  return apiGet<CreatorListFilters>(`${LIST_URL}/filters`)
}
// Performance — Overview/Profile와 동일하게 실 API만(실데이터 철칙, mock 없음).
//   hubPrograms는 portal 프록시가 응답을 Mongo 집계로 enrich해 채운다(별도 호출 없음).
//   platform은 선택 — 생략하면 BE가 youtube로 응답한다(파라미터 도입 전과 동일). 기존 호출부 무변경.
export async function fetchPerformance(id: string, platform?: Platform): Promise<PerformanceData> {
  return apiGet<PerformanceData>(`${BASE_URL}/${id}/performance`, { platform })
}
// dev 한정 mock 폴백 — 혜원 섹션(campaigns/financial/rateCard) 전용. 현정 섹션은 실 API만.
function devFallback<T>(mock: T) {
  return (err: unknown): T => {
    if (import.meta.env.DEV) {
      console.warn('[creators] 실 API 실패 → mock 폴백 (dev 전용):', err)
      return mock
    }
    throw err
  }
}
export async function fetchCampaignBoard(id: string): Promise<CampaignBoard> {
  if (useMock()) return MOCK.campaignBoard
  return apiGet<CampaignBoard>(`${BASE_URL}/${id}/campaigns`).catch(devFallback(MOCK.campaignBoard))
}
/** 내부 캠페인 드래프트 목록 (SF 미연동, isDraft=true). 실패 시 빈 배열. */
export async function fetchCampaignDrafts(id: string): Promise<Campaign[]> {
  if (useMock()) return []
  return apiGet<Campaign[]>(`${BASE_URL}/${id}/campaign-drafts`).catch(() => [])
}
/** 내부 캠페인 드래프트 생성. 생성된 드래프트(Campaign) 반환. */
export async function createCampaignDraft(id: string, input: NewCampaignInput): Promise<Campaign | null> {
  return apiSend<Campaign>('POST', `${BASE_URL}/${id}/campaign-drafts`, {
    salesforceCreatorId: id,
    brand: input.brand,
    title: input.title,
    owner: input.owner || null,
    budget: input.budget ?? null,
    startStage: input.startStage,
    commerceFusion: input.commerceFusion,
    startYm: input.periodStart || null,
    endYm: input.periodEnd || null,
    channels: input.channels,
  })
}
/** 내부 캠페인 드래프트 삭제. draftId = "draft:{n}"의 숫자 부분. */
export async function deleteCampaignDraft(id: string, draftId: number): Promise<void> {
  await apiSend('DELETE', `${BASE_URL}/${id}/campaign-drafts/${draftId}`)
}

/** 캠페인 발행 콘텐츠 링크 + 지표. 지표(views/watchRate/conversion)는 콘텐츠 분석 매칭 후속 → 현재 null. */
export interface CampaignContent {
  id: string
  campaignKey: string
  platform: CampaignChannelPlatform
  contentType?: string | null
  url: string
  memo?: string | null
  views?: number | null
  watchRate?: number | null
  conversion?: number | null
}
export async function fetchCampaignContents(id: string): Promise<CampaignContent[]> {
  if (useMock()) return []
  return apiGet<CampaignContent[]>(`${BASE_URL}/${id}/campaign-contents`).catch(() => [])
}
export async function addCampaignContent(
  id: string,
  input: { campaignKey: string; platform: CampaignChannelPlatform; contentType?: string; url: string; memo?: string },
): Promise<CampaignContent | null> {
  return apiSend<CampaignContent>('POST', `${BASE_URL}/${id}/campaign-contents`, {
    salesforceCreatorId: id,
    campaignKey: input.campaignKey,
    platform: input.platform,
    contentType: input.contentType || null,
    url: input.url,
    memo: input.memo || null,
  })
}
export async function deleteCampaignContent(id: string, contentId: string): Promise<void> {
  await apiSend('DELETE', `${BASE_URL}/${id}/campaign-contents/${contentId}`)
}

// ── 캠페인 앱-상태(진척) override — SF가 세분 못 하는 제작중→검수→발행→성과 진행 관리 ──
export async function setCampaignState(id: string, input: { campaignKey: string; progress: number; note?: string }): Promise<void> {
  await apiSend('POST', `${BASE_URL}/${id}/campaign-states`, {
    salesforceCreatorId: id,
    campaignKey: input.campaignKey,
    progress: input.progress,
    note: input.note || null,
  })
}
export async function clearCampaignState(id: string, campaignKey: string): Promise<void> {
  await apiSend('DELETE', `${BASE_URL}/${id}/campaign-states/${encodeURIComponent(campaignKey)}`)
}
export async function fetchFinancial(id: string): Promise<FinancialData> {
  if (useMock()) return MOCK.financial
  return apiGet<FinancialData>(`${BASE_URL}/${id}/financial`).catch(devFallback(MOCK.financial))
}
export async function fetchRateCard(id: string): Promise<RateCard> {
  if (useMock()) return MOCK.rateCard
  return apiGet<RateCard>(`${BASE_URL}/${id}/rate-card`).catch(devFallback(MOCK.rateCard))
}

// ── 단가표 편집(쓰기) — mock 모드에선 no-op(화면은 로컬 state로 낙관적 갱신) ──
async function apiSend<T>(method: 'PUT' | 'POST' | 'DELETE', path: string, body?: unknown): Promise<T | null> {
  const token = getLecommendToken()
  const res = await fetch(path, {
    method,
    headers: {
      Accept: 'application/json',
      ...(body ? { 'Content-Type': 'application/json' } : {}),
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    ...(body ? { body: JSON.stringify(body) } : {}),
  })
  let env: Envelope<T> | null = null
  try {
    env = (await res.json()) as Envelope<T>
  } catch {
    /* non-JSON */
  }
  if (res.status === 401) throw new ApiError('인증이 필요합니다. (lecommend 토큰 필요)', 401)
  if (!res.ok || !env?.success) throw new ApiError(env?.errorMessage ?? `요청 실패 (${res.status})`, res.status)
  return env.data ?? null
}

export async function saveRateProduct(creatorId: string, input: RateProductInput & { id?: string }): Promise<void> {
  if (useMock()) return
  await apiSend('PUT', `${BASE_URL}/${creatorId}/rate-card/products`, {
    salesforceCreatorId: creatorId,
    id: input.id ? Number(input.id) : null,
    channelKey: input.channelKey,
    type: input.type,
    sub: input.sub,
    priceLabel: input.price,
    priceAmount: null,
    options: input.options,
    note: input.note,
    desc: input.desc,
  })
}

export async function deleteRateProduct(creatorId: string, id: string): Promise<void> {
  if (useMock()) return
  await apiSend('DELETE', `${BASE_URL}/${creatorId}/rate-card/products/${id}`)
}

/** Salesforce 단가를 편집형으로 불러오기. 생성된 개수 반환(mock=0). */
export async function seedRateCard(creatorId: string): Promise<number> {
  if (useMock()) return 0
  return (await apiSend<number>('POST', `${BASE_URL}/${creatorId}/rate-card/seed`)) ?? 0
}

/** 허브 묶음 패키지 추가/수정 입력. */
export interface HubPackageInput {
  id?: string
  name: string
  items?: string
  subtotal?: number
  discount?: number
  finalPrice?: number
  revenueShare?: string
  notes: string[]
  lineItems: { label: string; price?: number }[]
}

export async function saveHubPackage(creatorId: string, input: HubPackageInput): Promise<void> {
  if (useMock()) return
  await apiSend('PUT', `${BASE_URL}/${creatorId}/rate-card/hub-packages`, {
    salesforceCreatorId: creatorId,
    id: input.id ? Number(input.id) : null,
    name: input.name,
    items: input.items,
    subtotal: input.subtotal ?? null,
    discount: input.discount ?? null,
    finalPrice: input.finalPrice ?? null,
    revenueShare: input.revenueShare,
    notes: input.notes,
    lineItems: input.lineItems.map((li, i) => ({
      label: li.label,
      priceLabel: li.price != null ? '₩' + li.price.toLocaleString('en-US') : null,
      priceAmount: li.price ?? null,
      sortOrder: i,
    })),
  })
}

export async function deleteHubPackage(creatorId: string, id: string): Promise<void> {
  if (useMock()) return
  await apiSend('DELETE', `${BASE_URL}/${creatorId}/rate-card/hub-packages/${id}`)
}

// ─────────────────────────────────────────────────────────────
// mock — 디자인 미리보기용 최소 샘플 (실데이터 연동 시 제거/축소)
// ─────────────────────────────────────────────────────────────
const MOCK = {
  // creator·overview·profile·performance mock 제거(실데이터 철칙) — 실 API만 호출.
  //   아래는 혜원 섹션(campaigns/financial/rateCard) 몫으로 유지.
  campaignBoard: {
    kpi: {
      inProgress: 5,
      dueThisWeek: 2,
      toUpload: 3,
      cumulativeViews: 84200000,
      commerceGmv: 320000000,
      totalRevenue: 0,
    },
    campaigns: [],
    schedule: [],
  } as CampaignBoard,
  financial: {
    totalGmv: 542000000,
    avgGmv: 135500000,
    topProject: 'espoir ₩1.82억',
    quarterly: [
      { label: '24 Q4', gmv: 121000000 },
      { label: '25 Q2', gmv: 143000000 },
      { label: '25 Q3', gmv: 96000000 },
      { label: '25 Q4', gmv: 182000000 },
    ],
    perProject: [
      { project: 'espoir 공동기획 세트', date: '2025.10', gmv: 182000000 },
      { project: 'TIRTIR 한정판 마스크', date: '2025.04', gmv: 143000000 },
      { project: '홀리데이 글로우 키트', date: '2024.11', gmv: 121000000 },
      { project: '자체 브랜드 데일리 립밤', date: '2025.08', gmv: 96000000 },
    ],
  } as FinancialData,
  rateCard: {
    hubPackages: [
      {
        id: 'hub-launch',
        name: '멀티채널 런칭 패키지',
        items: 'YouTube 롱폼 + Instagram 릴스 + TikTok 숏폼',
        subtotal: 23500000,
        discount: 3500000,
        finalPrice: 20000000,
        notes: ['3개 채널 동시 집행 기준', '촬영 1회차 통합 진행', '2차 활용은 별도 협의'],
        lineItems: [
          { label: 'YouTube 브랜디드 통합 영상 (롱폼)', price: 12000000 },
          { label: 'Instagram 릴스 1편', price: 5000000 },
          { label: 'TikTok 브랜디드 영상 1편', price: 4000000 },
          { label: '통합 기획·리포트', price: 2500000 },
        ],
      },
      {
        id: 'hub-commerce',
        name: '커머스 융합 패키지',
        items: '공동기획 + 라이브 커머스 + 시딩',
        subtotal: 18000000,
        discount: 6000000,
        finalPrice: 12000000,
        revenueShare: '판매액의 10% (R/S)',
        notes: ['라이브 1회 + 시딩 포함', '판매 수수료 별도', 'GMV 목표 협의 후 확정'],
        lineItems: [
          { label: '공동기획 콘텐츠', price: 8000000 },
          { label: '라이브 커머스 1회', price: 7000000 },
          { label: '제품 시딩·리뷰', price: 3000000 },
        ],
      },
    ],
    channels: [
      {
        key: 'youtube',
        name: 'YouTube',
        products: [
          {
            id: 'yt-1',
            type: '브랜디드 통합 영상',
            sub: '롱폼 · 8분 이상',
            price: '₩12,000,000',
            options: ['전용 기획·편집 포함', '2차 활용 +30%'],
            note: '최고 도달·설득력',
            desc: '제품 스토리텔링에 최적화된 단독 영상. 기획부터 편집까지 전담.',
          },
          { id: 'yt-2', type: '브랜디드 PPL', sub: '60초 내외', price: '₩6,000,000', options: ['본편 내 자연 노출'] },
          { id: 'yt-3', type: 'Shorts', sub: '숏폼', price: '₩4,000,000', options: ['세로형', '재활용 가능'] },
        ],
      },
      {
        key: 'instagram',
        name: 'Instagram',
        products: [
          { id: 'ig-1', type: '릴스', sub: '15~60초', price: '₩5,000,000', options: ['전용 기획'] },
          { id: 'ig-2', type: '피드 포스트', price: '₩3,000,000', options: ['이미지 3~5컷'] },
          { id: 'ig-3', type: '스토리', price: '₩1,500,000', options: ['링크 스티커'] },
        ],
      },
      {
        key: 'tiktok',
        name: 'TikTok',
        products: [{ id: 'tt-1', type: '브랜디드 영상', sub: '숏폼', price: '₩4,000,000', options: ['트렌드 포맷'] }],
      },
      {
        key: 'license',
        name: 'License',
        products: [{ id: 'lc-1', type: '2차 활용 라이선스', price: '별도 협의', options: ['기간·매체별 산정'] }],
      },
      {
        key: 'etc',
        name: 'Etc',
        products: [{ id: 'etc-1', type: '오프라인 행사·기타', price: '별도 협의', options: [] }],
      },
    ],
  } as RateCard,
}
